import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button } from '@atoms'
import Badge from '@components/atoms/Badge'
import Icon, { type IconTypes } from '@components/utils/Icon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@components/atoms/DropdownMenu'

// Real group#show building blocks, reused as-is (see notes in SupportersTab)
import { DialogProvider } from '@components/DialogProvider'
import SearchField from '@molecules/SearchField'
import { SelectField } from '@molecules/SelectField'
import { GroupShowSupporterCard } from '@frontend/contexts/supporters-management/features/supporters/GroupShowSupporterCard'
import {
  SupportersStats,
  ContactStats,
} from '@frontend/contexts/supporters-management/features/supporters/GroupShowStats'
import type { GroupMembersSearchQueryType } from '@frontend/graphql/queries/groups'

/**
 * Group show (design-phase prototype)
 *
 * A ground-up redesign of the groups#show page (v1: `action_groups/show.html.erb`),
 * built to pair on inside the real app shell using real v2 components. It explores
 * two things the team wants to standardise:
 *
 *   1. A simpler page header — group name on the left, primary action on the right —
 *      that could be reused across other v2 pages.
 *   2. A ShadCN-style navigation menu (Group Details · Supporters · Lists · Events)
 *      built on the same Radix Tabs primitive the `Tabs` atom wraps, with the tab
 *      visual style switchable in the design tools so we can compare options live.
 *
 * The "Group Details" tab is fleshed out into editable sections (Group information,
 * Membership criteria, Public page, Group leaders). Supporters / Lists / Events are
 * intentionally left as empty placeholders. Sample data only — no model or GraphQL
 * behind it. The "Design tools" overlay at the bottom-left is dev-only.
 */

type NavStyle = 'underline' | 'pills' | 'segmented'
type Density = 'comfortable' | 'compact'
// On the Supporters tab the view action bar and the stats row both want the top
// of the view. How they coexist:
//  - stacked:    the action bar (title + actions, with its divider) sits above a
//                separate stats-cards row
//  - integrated: the title + actions and the stats cards form one header block,
//                no divider between them
//  - hidden:     no stats — just the action bar
type StatsLayout = 'stacked' | 'integrated' | 'hidden'
// Communications: how the view action bar and the sub-nav (Inbox / Email / SMS)
// relate at the top of the tab.
//  - stacked:    action bar, then the sub-nav pills on their own row below
//  - integrated: title + actions and the sub-nav share one header block (no divider between)
//  - subnavOnly: drop the titled bar — the sub-nav sits left, the context action right
type CommsLayout = 'stacked' | 'integrated' | 'subnavOnly'
// What the ViewActionBar shows on its left (explored across every tab).
type BarVariant = 'titleDescription' | 'titleOnly' | 'descriptionOnly' | 'actionsOnly'

// A ShadCN navigation-menu style dropdown item hung off a tab. Selecting one
// activates the tab and jumps to the matching section within its panel.
type SubItem = { value: string; label: string; description: string; icon: IconTypes }
type TabDef = {
  value: string
  label: string
  icon: IconTypes
  count?: number
  countTone?: 'neutral' | 'warning'
  subItems?: SubItem[]
}

const TABS: TabDef[] = [
  { value: 'details', label: 'Group Details', icon: 'info-circle' },
  { value: 'supporters', label: 'Supporters', icon: 'users-01', count: 248 },
  { value: 'lists', label: 'Lists', icon: 'clipboard-minus', count: 6 },
  { value: 'events', label: 'Events', icon: 'calendar', count: 3 },
]

// Message inboxes + analytics. The awaiting-response counts are what make these
// time-sensitive, so every placement keeps them visible somewhere.
const INBOXES: Array<{ key: string; label: string; icon: IconTypes; awaiting: number }> = [
  { key: 'email', label: 'Email inbox', icon: 'mail-01', awaiting: 5 },
  { key: 'sms', label: 'SMS inbox', icon: 'message-chat-square', awaiting: 2 },
]
const TOTAL_AWAITING = INBOXES.reduce((n, i) => n + i.awaiting, 0)

const COMMS_SUB_ITEMS: SubItem[] = [
  { value: 'inbox', label: 'Inbox', description: 'Email & SMS conversations with supporters.', icon: 'message-chat-square' },
  { value: 'email-blasts', label: 'Email blasts', description: 'Bulk emails sent to this group.', icon: 'mail-01' },
  { value: 'sms-blasts', label: 'SMS blasts', description: 'Bulk text messages sent to this group.', icon: 'send' },
]
const COMMS_TAB: TabDef = {
  value: 'communications',
  label: 'Communications',
  icon: 'message-chat-square',
  count: TOTAL_AWAITING,
  countTone: 'warning',
  subItems: COMMS_SUB_ITEMS,
}

// Sample communications data for the Communications tab.
type InboxChannel = 'email' | 'sms'
const INBOX_THREADS: Array<{ channel: InboxChannel; name: string; snippet: string; awaiting: boolean; time: string }> = [
  { channel: 'email', name: 'Maria Rossi', snippet: 'Is there parking near the venue on Saturday?', awaiting: true, time: '14m' },
  { channel: 'sms', name: 'Luca Bianchi', snippet: 'Do I need to bring my membership card?', awaiting: true, time: '28m' },
  { channel: 'email', name: 'Elena Costa', snippet: 'Poll watching on the Sunday works for me.', awaiting: true, time: '2h' },
  { channel: 'sms', name: 'Paolo Gallo', snippet: 'Perfect, see you at 10am.', awaiting: false, time: '1d' },
]

const EMAIL_BLASTS: Array<{ subject: string; sentAt: string; recipients: number; openRate: string }> = [
  { subject: 'June assembly — final details', sentAt: '2 Jul 2026', recipients: 723, openRate: '54%' },
  { subject: 'Volunteer call: election weekend', sentAt: '24 Jun 2026', recipients: 512, openRate: '61%' },
  { subject: 'Welcome to North Ward Volunteers', sentAt: '10 Jun 2026', recipients: 248, openRate: '72%' },
]

const SMS_BLASTS: Array<{ message: string; sentAt: string; recipients: number }> = [
  { message: 'Reminder: assembly tonight at 6:15pm, Riverside Hall. See you there!', sentAt: '4 Jul 2026', recipients: 512 },
  { message: 'Doors open 5pm Saturday — bring a friend who wants to get involved.', sentAt: '20 Jun 2026', recipients: 301 },
]

// Sample group, standing in for an ActionGroup record.
const GROUP = {
  name: 'North Ward Volunteers',
  location: 'Riverside, District 4',
  memberCount: 248,
  status: 'Active',
  criteriaTags: ['north-ward', 'volunteer-2026', 'door-knocker'],
  publicUrl: 'https://example.org/groups/north-ward-volunteers',
  publicDescription:
    'North Ward Volunteers brings neighbours together to knock doors, host local ' +
    'assemblies and get out the vote across Riverside. New members always welcome — ' +
    'come to a meeting and see what we do.',
  leaders: [
    { name: 'Amara Okafor', role: 'Captain', email: 'amara.okafor@example.org' },
    { name: 'Tom Whitfield', role: 'Vice-captain', email: 'tom.whitfield@example.org' },
    { name: 'Priya Nair', role: 'Coordinator', email: 'priya.nair@example.org' },
  ],
}

const GroupShowPreview = () => {
  const [tab, setTab] = useState('details')

  // Design-exploration toggles (floating dev overlay)
  const [showDesign, setShowDesign] = useState(false)
  const [navStyle, setNavStyle] = useState<NavStyle>('underline')
  const [density, setDensity] = useState<Density>('comfortable')
  const [statsLayout, setStatsLayout] = useState<StatsLayout>('stacked')
  const [commsLayout, setCommsLayout] = useState<CommsLayout>('stacked')
  const [barVariant, setBarVariant] = useState<BarVariant>('titleDescription')
  const [listInCard, setListInCard] = useState(false) // default: borderless, content sits on the page
  // Which section to open inside a tab after picking a dropdown item.
  // The nonce forces the effect to re-run even when the same item is re-picked.
  const [tabFocus, setTabFocus] = useState<{ section: string; nonce: number } | null>(null)

  const selectSub = (tabValue: string, section: string) => {
    setTab(tabValue)
    setTabFocus((f) => ({ section, nonce: (f?.nonce ?? 0) + 1 }))
  }

  const compact = density === 'compact'
  // Committed: Communications is a first-class tab (the "Comms tab" solution).
  const navTabs = [...TABS, COMMS_TAB]

  return (
    <PageLayout.Root>
      <PageLayout.Body>
        <div className={clsx('tw:mx-auto tw:flex tw:max-w-5xl tw:flex-col', compact ? 'tw:gap-4' : 'tw:gap-6')}>
          {/* ---- Simpler, reusable page header ---- */}
          <PageHeader />

          {/* ---- ShadCN-style navigation menu (tab dropdowns always on) ---- */}
          <TabsPrimitive.Root value={tab} onValueChange={setTab} activationMode="manual">
            <PreviewTabsList navStyle={navStyle} items={navTabs} onSelectSub={selectSub} />

            <div className={clsx(compact ? 'tw:mt-4' : 'tw:mt-6')}>
              <TabsPrimitive.Content value="details" className="tw:focus-visible:outline-hidden">
                <DetailsTab compact={compact} barVariant={barVariant} />
              </TabsPrimitive.Content>

              <TabsPrimitive.Content value="supporters" className="tw:focus-visible:outline-hidden">
                <SupportersTab compact={compact} statsLayout={statsLayout} bordered={listInCard} barVariant={barVariant} />
              </TabsPrimitive.Content>

              <TabsPrimitive.Content value="lists" className="tw:focus-visible:outline-hidden">
                <ListsTab compact={compact} barVariant={barVariant} />
              </TabsPrimitive.Content>

              <TabsPrimitive.Content value="events" className="tw:focus-visible:outline-hidden">
                <EventsTab compact={compact} barVariant={barVariant} />
              </TabsPrimitive.Content>

              <TabsPrimitive.Content value="communications" className="tw:focus-visible:outline-hidden">
                <CommunicationsTab compact={compact} focus={tabFocus} layout={commsLayout} barVariant={barVariant} />
              </TabsPrimitive.Content>
            </div>
          </TabsPrimitive.Root>
        </div>

        {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
        {createPortal(
          <>
            <button
              onClick={() => setShowDesign((s) => !s)}
              className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
            >
              <Icon type="eye" className="tw:h-4 tw:w-4" />
              {showDesign ? 'Hide design tools' : 'Design tools'}
            </button>
            {showDesign && (
              <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
                <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                  <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                  <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                  <button
                    onClick={() => setShowDesign(false)}
                    aria-label="Hide design tools"
                    className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                  >
                    <Icon type="x-close" className="tw:h-4 tw:w-4" />
                  </button>
                </div>
                <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                  This panel is purely for exploring the design and is not part of the product.
                  <br />
                  Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
                </p>
                <div className="tw:flex tw:flex-wrap tw:gap-8">
                  <SegControl
                    label="Navigation style"
                    value={navStyle}
                    onChange={(v) => setNavStyle(v as NavStyle)}
                    options={[
                      { value: 'underline', label: 'Underline' },
                      { value: 'pills', label: 'Pills' },
                      { value: 'segmented', label: 'Segmented' },
                    ]}
                  />
                  <SegControl
                    label="Supporters: stats + bar"
                    value={statsLayout}
                    onChange={(v) => setStatsLayout(v as StatsLayout)}
                    options={[
                      { value: 'stacked', label: 'Stacked' },
                      { value: 'integrated', label: 'Integrated' },
                      { value: 'hidden', label: 'Hide stats' },
                    ]}
                  />
                  <SegControl
                    label="Communications: subnav + bar"
                    value={commsLayout}
                    onChange={(v) => setCommsLayout(v as CommsLayout)}
                    options={[
                      { value: 'stacked', label: 'Stacked' },
                      { value: 'integrated', label: 'Integrated' },
                      { value: 'subnavOnly', label: 'Subnav only' },
                    ]}
                  />
                  <SegControl
                    label="Action bar content"
                    value={barVariant}
                    onChange={(v) => setBarVariant(v as BarVariant)}
                    options={[
                      { value: 'titleDescription', label: 'Title + desc' },
                      { value: 'titleOnly', label: 'Title' },
                      { value: 'descriptionOnly', label: 'Description' },
                      { value: 'actionsOnly', label: 'Actions' },
                    ]}
                  />
                  <SegControl
                    label="Supporters list"
                    value={listInCard ? 'card' : 'plain'}
                    onChange={(v) => setListInCard(v === 'card')}
                    options={[
                      { value: 'plain', label: 'On page' },
                      { value: 'card', label: 'In card' },
                    ]}
                  />
                  <SegControl
                    label="Density"
                    value={density}
                    onChange={(v) => setDensity(v as Density)}
                    options={[
                      { value: 'comfortable', label: 'Comfortable' },
                      { value: 'compact', label: 'Compact' },
                    ]}
                  />
                </div>
              </div>
            )}
          </>,
          document.body
        )}
      </PageLayout.Body>
    </PageLayout.Root>
  )
}

// ---- Header: group name (with subtitle) left, primary action right ----
// Analytics is group-wide (not a tab), so it sits here; the inbox lives in the
// committed Communications tab.
function PageHeader() {
  return (
    <header className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4">
      <div className="tw:flex tw:items-center tw:gap-3">
        <span className="tw:flex tw:h-11 tw:w-11 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-xl tw:bg-primary-50 tw:text-primary-600">
          <Icon type="users-01" className="tw:h-6 tw:w-6" aria-hidden />
        </span>
        <div>
          <h1 className="tw:text-xl tw:font-bold tw:text-gray-900">{GROUP.name}</h1>
          <p className="tw:mt-0.5 tw:text-sm tw:text-gray-500">
            {GROUP.location} · {GROUP.memberCount} supporters
          </p>
        </div>
      </div>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Button rank="secondary" leadingIcon="line-chart-up">
          Analytics
        </Button>
        <Button rank="primary" leadingIcon="plus">
          Add new supporter
        </Button>
      </div>
    </header>
  )
}

// Small amber "awaiting response" count badge, shared across the comms views.
function AwaitingCount({ n, className }: { n: number; className?: string }) {
  if (!n) return null
  return (
    <span
      title={`${n} awaiting response`}
      className={clsx(
        'tw:inline-flex tw:h-5 tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:bg-warning-50 tw:px-1.5 tw:text-xs tw:font-semibold tw:text-warning-700',
        className
      )}
    >
      {n}
    </span>
  )
}

// ---- Tab navigation: one map, per-style class config ----
// (Collapsed from three near-identical branches so the trigger markup — and the
// dropdown wiring below — lives in exactly one place.)
const NAV_STYLES: Record<NavStyle, { list: string; trigger: string }> = {
  underline: {
    list: 'tw:flex tw:items-center tw:gap-1 tw:border-b tw:border-gray-200',
    trigger: clsx(
      'tw:relative tw:-mb-px tw:inline-flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:border-b-2 tw:border-transparent tw:px-4 tw:py-3 tw:text-sm tw:font-medium tw:text-gray-600 tw:transition-colors',
      'tw:hover:text-gray-900',
      'tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
      'tw:data-[state=active]:border-primary-600 tw:data-[state=active]:text-primary-700'
    ),
  },
  pills: {
    list: 'tw:inline-flex tw:items-center tw:gap-1 tw:rounded-lg tw:bg-gray-cool-50 tw:p-1',
    trigger: clsx(
      'tw:inline-flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:rounded-md tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:text-gray-600 tw:transition-colors',
      'tw:hover:text-gray-900',
      'tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
      'tw:data-[state=active]:bg-white tw:data-[state=active]:text-primary-700 tw:data-[state=active]:shadow-sm'
    ),
  },
  segmented: {
    list: 'tw:inline-flex tw:items-center',
    trigger: clsx(
      'tw:relative tw:inline-flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:border tw:border-gray-300 tw:px-4 tw:py-2.5 tw:text-sm tw:font-medium tw:text-gray-700',
      'tw:-mr-px tw:first-of-type:rounded-l-lg tw:last-of-type:mr-0 tw:last-of-type:rounded-r-lg',
      'tw:focus-visible:z-10 tw:focus-visible:outline-hidden tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
      'tw:data-[state=active]:z-10 tw:data-[state=active]:bg-primary-500 tw:data-[state=active]:text-white'
    ),
  },
}

function PreviewTabsList({
  navStyle,
  items,
  onSelectSub,
}: {
  navStyle: NavStyle
  items: TabDef[]
  onSelectSub: (tab: string, section: string) => void
}) {
  const s = NAV_STYLES[navStyle]
  return (
    <TabsPrimitive.List className={s.list}>
      {items.map((t) => (
        <TabItem
          key={t.value}
          tab={t}
          triggerClass={s.trigger}
          hasMenu={Boolean(t.subItems?.length)}
          onSelectSub={onSelectSub}
        />
      ))}
    </TabsPrimitive.List>
  )
}

// A tab trigger, optionally wearing a ShadCN navigation-menu style dropdown.
function TabItem({
  tab,
  triggerClass,
  hasMenu,
  onSelectSub,
}: {
  tab: TabDef
  triggerClass: string
  hasMenu: boolean
  onSelectSub: (tab: string, section: string) => void
}) {
  const [open, setOpen] = useState(false)

  const trigger = (
    <TabsPrimitive.Trigger value={tab.value} className={triggerClass}>
      <Icon type={tab.icon} className="tw:h-4 tw:w-4" aria-hidden />
      {tab.label}
      {tab.count != null && <TabCount count={tab.count} tone={tab.countTone} />}
      {hasMenu && (
        <Icon
          type="chevron-down"
          className={clsx('tw:h-3.5 tw:w-3.5 tw:transition-transform', open && 'tw:rotate-180')}
          aria-hidden
        />
      )}
    </TabsPrimitive.Trigger>
  )

  if (!hasMenu) return trigger

  return (
    <span
      className="tw:relative tw:inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false)
      }}
    >
      {trigger}
      {open && (
        // tw:pt-2 keeps a hover bridge between the trigger and the panel
        <div className="tw:absolute tw:left-0 tw:top-full tw:z-30 tw:pt-2">
          <div className="tw:w-[min(24rem,calc(100vw-3rem))] tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-2 tw:shadow-lg">
            {tab.subItems!.map((sub) => (
              <button
                key={sub.value}
                type="button"
                onClick={() => {
                  onSelectSub(tab.value, sub.value)
                  setOpen(false)
                }}
                className="tw:flex tw:w-full tw:items-start tw:gap-3 tw:rounded-lg tw:p-3 tw:text-left tw:hover:bg-gray-cool-50 tw:focus-visible:bg-gray-cool-50 tw:focus-visible:outline-hidden"
              >
                <span className="tw:mt-0.5 tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-primary-50 tw:text-primary-600">
                  <Icon type={sub.icon} className="tw:h-4 tw:w-4" aria-hidden />
                </span>
                <span className="tw:min-w-0">
                  <span className="tw:block tw:text-sm tw:font-semibold tw:text-gray-900">{sub.label}</span>
                  <span className="tw:block tw:text-xs tw:text-gray-500">{sub.description}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </span>
  )
}

function TabCount({ count, tone = 'neutral' }: { count: number; tone?: 'neutral' | 'warning' }) {
  return (
    <span
      className={clsx(
        'tw:rounded-full tw:px-1.5 tw:py-0.5 tw:text-xs tw:font-semibold',
        tone === 'warning' ? 'tw:bg-warning-50 tw:text-warning-700' : 'tw:bg-gray-100 tw:text-gray-600'
      )}
    >
      {count}
    </span>
  )
}

// ---- Group Details tab ----
function DetailsTab({ compact, barVariant }: { compact: boolean; barVariant: BarVariant }) {
  return (
    <div className={clsx('tw:flex tw:flex-col', compact ? 'tw:gap-3' : 'tw:gap-4')}>
      <ViewActionBar
        variant={barVariant}
        title="Group details"
        subtitle="Settings and information for this group."
        actions={
          <>
            <Button rank="secondary" leadingIcon="pencil-02">
              Edit group
            </Button>
            <ActionMenu
              actions={[
                { label: 'View public page', icon: 'external-link' },
                { label: 'Group history', icon: 'clock' },
                { label: 'Duplicate group', icon: 'copy-06' },
              ]}
            />
          </>
        }
      />

      <Section
        icon="info-circle"
        title="Group information"
        description="Name, location and status of this group."
        compact={compact}
      >
        <dl className="tw:grid tw:grid-cols-1 tw:gap-x-8 tw:gap-y-4 tw:sm:grid-cols-2">
          <Field label="Name" value={GROUP.name} />
          <Field label="Location" value={GROUP.location} />
          <Field label="Status">
            <Badge color="success" leadingIcon="check-circle" bordered>
              {GROUP.status}
            </Badge>
          </Field>
          <Field label="Supporters" value={`${GROUP.memberCount}`} />
        </dl>
      </Section>

      <Section
        icon="tag-01"
        title="Membership criteria"
        description="Supporters are added to this group when they match these tags."
        compact={compact}
      >
        <div className="tw:flex tw:flex-wrap tw:gap-2">
          {GROUP.criteriaTags.map((tag) => (
            <Badge key={tag} color="primary" leadingIcon="tag-01">
              {tag}
            </Badge>
          ))}
        </div>
      </Section>

      <Section
        icon="eye"
        title="Public page"
        description="What supporters see on the group's public sign-up page."
        compact={compact}
        action={
          <div className="tw:flex tw:items-center tw:gap-2">
            <a
              href={GROUP.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:font-medium tw:text-primary-700 tw:hover:text-primary-900"
            >
              <Icon type="external-link" className="tw:h-4 tw:w-4" aria-hidden />
              View public page
            </a>
            <EditButton />
          </div>
        }
      >
        <p className="tw:max-w-prose tw:text-sm tw:leading-relaxed tw:text-gray-600">{GROUP.publicDescription}</p>
      </Section>

      <Section
        icon="mail-01"
        title="Sender email"
        description="The from-address supporters see when this group emails them."
        compact={compact}
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <span className="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-900">
            <Icon type="mail-01" className="tw:h-4 tw:w-4 tw:text-gray-400" aria-hidden />
            north-ward@example.org
          </span>
          <Badge color="gray">Using group default</Badge>
        </div>
      </Section>

      <Section
        icon="user-square"
        title="Group leaders"
        description="Captains, vice-captains and coordinators who manage this group."
        compact={compact}
        action={
          <Button rank="secondary" size="sm" leadingIcon="plus">
            Manage leaders
          </Button>
        }
      >
        <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
          {GROUP.leaders.map((leader) => (
            <li key={leader.email} className="tw:flex tw:items-center tw:gap-3 tw:py-3 tw:first:pt-0 tw:last:pb-0">
              <span className="tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary-50 tw:text-sm tw:font-semibold tw:text-primary-700">
                {leader.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </span>
              <div className="tw:min-w-0">
                <div className="tw:text-sm tw:font-medium tw:text-gray-900">{leader.name}</div>
                <div className="tw:truncate tw:text-xs tw:text-gray-500">{leader.email}</div>
              </div>
              <Badge color="gray" className="tw:ml-auto">
                {leader.role}
              </Badge>
            </li>
          ))}
        </ul>
      </Section>

      {/* Danger zone — rare, destructive, group-level action lives at the foot of
          the group's "settings" tab rather than in a tab or the header. */}
      <section className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:rounded-xl tw:border tw:border-error-200 tw:bg-error-50 tw:px-5 tw:py-4">
        <div>
          <h2 className="tw:text-sm tw:font-bold tw:text-error-700">Archive this group</h2>
          <p className="tw:mt-0.5 tw:text-xs tw:text-error-600">
            The group is hidden from listings and its public page. Supporters are not deleted.
          </p>
        </div>
        <Button rank="secondary" color="danger" size="sm" leadingIcon="trash-01">
          Archive group
        </Button>
      </section>
    </div>
  )
}

// ---- Supporters tab ----------------------------------------------------
// Reuses the real group#show pieces: the stats cards (GroupShowStats), the
// supporter card (GroupShowSupporterCard) and the SearchField / SelectField molecules.
// The production search/sort/filter controls are wired to a zustand store that
// fires Inertia server reloads (useSupportersSearch), so they can't filter the
// local sample data — here the same molecules are driven by local state instead,
// so search, sort, the view toggle and pagination all work standalone. The
// DialogProvider is given an empty registry so the card's action buttons render
// but stay inert (no GraphQL behind them).

type SupporterEdge = GroupMembersSearchQueryType['group']['supporters']['edges'][number]

const SUPPORTERS = [
  {
    node: {
      id: '1', firstName: 'Londa', lastName: 'Abshire', email: 'londa.abshire@example.com',
      primaryMobile: '+61 400 111 222', location: 'Adelaide, QLD 4272, AU', federalDistrict: 'Wright',
      engagementLevel: 'Engagement Level 1', birthdate: null, lastContactedAt: '2025-09-25', rsvpCount: 0,
      note: 'NOTE!', tags: { nodes: [{ name: 'engagement-level-1' }, { name: 'tag-filter-1' }, { name: 'supporterbase' }, { name: 'Adelaide' }] },
    },
    membership: { createdAt: '2025-05-20', emailable: true, textable: true, callable: true },
  },
  {
    node: {
      id: '2', firstName: 'Marcus', lastName: 'Hartley', email: 'm.hartley@example.com',
      primaryMobile: '+61 400 333 444', location: 'Brisbane, QLD 4000, AU', federalDistrict: 'Griffith',
      engagementLevel: 'Engagement Level 3', birthdate: null, lastContactedAt: '2026-06-14', rsvpCount: 4,
      note: null, tags: { nodes: [{ name: 'volunteer' }, { name: 'door-knocker' }, { name: 'supporterbase' }] },
    },
    membership: { createdAt: '2024-11-02', emailable: true, textable: false, callable: true },
  },
  {
    node: {
      id: '3', firstName: 'Priya', lastName: 'Nair', email: 'priya.nair@example.com',
      primaryMobile: '+61 400 555 666', location: 'Gold Coast, QLD 4217, AU', federalDistrict: 'Moncrieff',
      engagementLevel: 'Engagement Level 2', birthdate: null, lastContactedAt: '2026-07-01', rsvpCount: 2,
      note: 'Offered to host a house meeting in July.', tags: { nodes: [{ name: 'host' }, { name: 'engagement-level-2' }, { name: 'supporterbase' }] },
    },
    membership: { createdAt: '2025-02-18', emailable: true, textable: true, callable: false },
  },
  {
    node: {
      id: '4', firstName: 'Tom', lastName: 'Whitfield', email: 't.whitfield@example.com',
      primaryMobile: '+61 400 777 888', location: 'Ipswich, QLD 4305, AU', federalDistrict: 'Blair',
      engagementLevel: 'Engagement Level 1', birthdate: null, lastContactedAt: null, rsvpCount: 0,
      note: null, tags: { nodes: [{ name: 'new-signup' }, { name: 'supporterbase' }] },
    },
    membership: { createdAt: '2026-06-30', emailable: true, textable: true, callable: true },
  },
  {
    node: {
      id: '5', firstName: 'Amara', lastName: 'Okafor', email: 'amara.okafor@example.com',
      primaryMobile: '+61 400 999 000', location: 'Toowoomba, QLD 4350, AU', federalDistrict: 'Groom',
      engagementLevel: 'Engagement Level 4', birthdate: null, lastContactedAt: '2026-06-28', rsvpCount: 9,
      note: 'Group captain — key organiser for the northern branch.', tags: { nodes: [{ name: 'captain' }, { name: 'volunteer-2026' }, { name: 'supporterbase' }] },
    },
    membership: { createdAt: '2023-08-11', emailable: true, textable: true, callable: true },
  },
  {
    node: {
      id: '6', firstName: 'Jia', lastName: 'Chen', email: 'jia.chen@example.com',
      primaryMobile: '+61 401 222 333', location: 'Cairns, QLD 4870, AU', federalDistrict: 'Leichhardt',
      engagementLevel: 'Engagement Level 2', birthdate: null, lastContactedAt: '2026-05-19', rsvpCount: 1,
      note: null, tags: { nodes: [{ name: 'donor' }, { name: 'engagement-level-2' }, { name: 'supporterbase' }] },
    },
    membership: { createdAt: '2025-09-04', emailable: false, textable: true, callable: true },
  },
] as unknown as SupporterEdge[]

const SUPPORTER_PAGE_SIZE = 3

type SupporterSort = 'name_asc' | 'name_desc' | 'recent_contact' | 'recent_join' | 'rsvp_desc'
const SUPPORTER_SORT_OPTIONS: Array<{ value: SupporterSort; label: string }> = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'recent_contact', label: 'Recently contacted' },
  { value: 'recent_join', label: 'Recently joined' },
  { value: 'rsvp_desc', label: 'Most RSVPs' },
]

function sortSupporters(edges: SupporterEdge[], sort: SupporterSort): SupporterEdge[] {
  const ts = (d?: string | null) => (d ? new Date(d).getTime() : 0)
  const byName = (a: SupporterEdge, b: SupporterEdge) =>
    `${a.node.firstName} ${a.node.lastName}`.localeCompare(`${b.node.firstName} ${b.node.lastName}`)
  const copy = [...edges]
  switch (sort) {
    case 'name_desc':
      return copy.sort((a, b) => byName(b, a))
    case 'recent_contact':
      return copy.sort((a, b) => ts(b.node.lastContactedAt) - ts(a.node.lastContactedAt))
    case 'recent_join':
      return copy.sort((a, b) => ts(b.membership?.createdAt) - ts(a.membership?.createdAt))
    case 'rsvp_desc':
      return copy.sort((a, b) => (b.node.rsvpCount ?? 0) - (a.node.rsvpCount ?? 0))
    default:
      return copy.sort(byName)
  }
}

// The four group-level supporter actions, surfaced from the old "More actions"
// dropdown. "Advanced filters" (saved AdvancedFilter queries) is a distinct
// feature from the toolbar's ad-hoc "Add filters".
const SUPPORTER_ACTIONS: Array<{ key: string; label: string; icon: IconTypes }> = [
  { key: 'sync', label: 'Sync from NationBuilder', icon: 'refresh' },
  { key: 'preferences', label: 'Contact preferences', icon: 'user-x' },
  { key: 'filters', label: 'Advanced filters', icon: 'funnel' },
  { key: 'export', label: 'Export', icon: 'download-cloud' },
]

// A single "Actions" dropdown holding all four supporter actions. The same
// control is dropped into whichever anchor the placement toggle selects, so the
// comparison is purely about *where* it lives, not how the buttons are grouped.
function SupporterActionsMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button rank="secondary" leadingIcon="dots-horizontal" trailingIcon="chevron-down">
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="tw:min-w-56">
        {SUPPORTER_ACTIONS.map((a) => (
          <DropdownMenuItem key={a.key} onSelect={() => {}}>
            <Icon type={a.icon} className="tw:text-gray-500" />
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SupportersTab({
  compact,
  statsLayout,
  bordered,
  barVariant,
}: {
  compact: boolean
  statsLayout: StatsLayout
  bordered: boolean
  barVariant: BarVariant
}) {
  const showStats = statsLayout !== 'hidden'
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SupporterSort>('name_asc')
  const [view, setView] = useState<'card' | 'table'>('card')
  const [page, setPage] = useState(1)

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? SUPPORTERS.filter((e) => `${e.node.firstName} ${e.node.lastName}`.toLowerCase().includes(q))
      : SUPPORTERS
    return sortSupporters(filtered, sort)
  }, [query, sort])

  const totalPages = Math.max(1, Math.ceil(results.length / SUPPORTER_PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = results.slice((currentPage - 1) * SUPPORTER_PAGE_SIZE, currentPage * SUPPORTER_PAGE_SIZE)

  const stats = (
    <div className="tw:flex tw:flex-wrap tw:gap-4">
      <SupportersStats total={723} last30Days={12} />
      <ContactStats emails={34} sms={12} calls={8} />
    </div>
  )
  const actions = <SupporterActionsMenu />
  // 'integrated' with stats present: title + actions and the stat cards share one
  // header block (no divider). Otherwise the standard action bar, with the stats
  // stacked below it as a separate row.
  const integrated = statsLayout === 'integrated' && showStats

  return (
    <DialogProvider registry={{}}>
      <div className={clsx('tw:flex tw:flex-col', compact ? 'tw:gap-4' : 'tw:gap-6')}>
        {integrated ? (
          <div className="tw:flex tw:flex-col tw:gap-4 tw:border-b tw:border-gray-200 tw:pb-4">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
              <BarHeading variant={barVariant} title="Supporters" description="People in this group." />
              {actions}
            </div>
            {stats}
          </div>
        ) : (
          <>
            <ViewActionBar variant={barVariant} title="Supporters" subtitle="People in this group." actions={actions} />
            {showStats && stats}
          </>
        )}

        {/* Toolbar + results. Bordered card, or borderless so it sits on the page. */}
        <div
          className={clsx(
            'tw:w-full',
            bordered
              ? 'tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:shadow-xs'
              : 'tw:flex tw:flex-col tw:gap-4'
          )}
        >
          <div className={clsx('tw:flex tw:flex-wrap tw:items-end tw:gap-4', bordered && 'tw:p-6')}>
            <div className="tw:min-w-[16rem] tw:flex-1">
              <SearchField
                fieldName="preview_supporter_name"
                displayName="Search supporters by name"
                placeholder="Type to search by name"
                defaultValue=""
                onChange={(e) => {
                  setQuery(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            <div className="tw:w-max tw:shrink-0">
              <SelectField<SupporterSort>
                name="sort_by"
                label="Sort by"
                options={SUPPORTER_SORT_OPTIONS}
                value={sort}
                onValueChange={setSort}
              />
            </div>
            <Button rank="secondary" leadingIcon="filter-lines">
              Add filters
            </Button>
            <div
              role="group"
              aria-label="Supporter view"
              className="tw:grid tw:shrink-0 tw:grid-cols-2 tw:overflow-hidden tw:rounded-lg"
            >
              <Button
                rank={view === 'card' ? 'primary' : 'secondary'}
                leadingIcon="users-01"
                onClick={() => setView('card')}
                className="tw:rounded-none tw:border-0"
                aria-pressed={view === 'card'}
              >
                Card view
              </Button>
              <Button
                rank={view === 'table' ? 'primary' : 'secondary'}
                leadingIcon="dot-points"
                onClick={() => setView('table')}
                className="tw:rounded-none tw:border-0"
                aria-pressed={view === 'table'}
              >
                Table view
              </Button>
            </div>
          </div>

          {view === 'card' ? (
            <div role="list" className={clsx('tw:flex tw:flex-col tw:gap-4', bordered && 'tw:bg-white tw:p-4')}>
              {pageItems.map((edge, index) => (
                <GroupShowSupporterCard
                  {...edge}
                  key={edge.node.id}
                  groupId="preview-group"
                  dataCompId={`preview-supporter-card-${index}`}
                  electoralFiltersEnabled
                  showAllTags
                />
              ))}
            </div>
          ) : (
            <SupporterTable edges={pageItems} />
          )}

          {results.length === 0 && (
            <div className={clsx('tw:p-10 tw:text-center tw:text-sm tw:text-gray-500', bordered && 'tw:bg-white')}>
              No supporters match “{query}”.
            </div>
          )}
        </div>

        {/* Pagination — local, 3 per page */}
        {results.length > 0 && (
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
            <span className="tw:text-sm tw:text-gray-500">
              {(currentPage - 1) * SUPPORTER_PAGE_SIZE + 1}–{Math.min(currentPage * SUPPORTER_PAGE_SIZE, results.length)} of{' '}
              {results.length}
            </span>
            {totalPages > 1 && <SupporterPager page={currentPage} totalPages={totalPages} onPage={setPage} />}
          </div>
        )}
      </div>
    </DialogProvider>
  )
}

// Lightweight table view. The production SupporterDataTable is built on a
// tanstack table instance from useSupporterTable (dialogs, column-visibility,
// Inertia pagination), which is too coupled to drive from local sample data —
// so the alternate view is a plain table over the same dummy records.
function SupporterTable({ edges }: { edges: SupporterEdge[] }) {
  return (
    <div className="tw:overflow-x-auto tw:bg-white">
      <table className="tw:w-full tw:text-left tw:text-sm">
        <thead>
          <tr className="tw:border-t tw:border-gray-200 tw:bg-gray-cool-50 tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-500">
            <th className="tw:px-4 tw:py-3 tw:font-medium">Name</th>
            <th className="tw:px-4 tw:py-3 tw:font-medium">Location</th>
            <th className="tw:px-4 tw:py-3 tw:font-medium">Engagement</th>
            <th className="tw:px-4 tw:py-3 tw:font-medium">Joined</th>
            <th className="tw:px-4 tw:py-3 tw:font-medium">Last contacted</th>
            <th className="tw:px-4 tw:py-3 tw:text-right tw:font-medium">RSVPs</th>
          </tr>
        </thead>
        <tbody>
          {edges.map((e) => (
            <tr key={e.node.id} className="tw:border-t tw:border-gray-100 tw:text-gray-900">
              <td className="tw:px-4 tw:py-3 tw:font-medium">
                {e.node.firstName} {e.node.lastName}
              </td>
              <td className="tw:px-4 tw:py-3 tw:text-gray-600">{e.node.location}</td>
              <td className="tw:px-4 tw:py-3 tw:text-gray-600">{e.node.engagementLevel}</td>
              <td className="tw:px-4 tw:py-3 tw:text-gray-600">{e.membership?.createdAt ?? '—'}</td>
              <td className="tw:px-4 tw:py-3 tw:text-gray-600">{e.node.lastContactedAt ?? 'Never'}</td>
              <td className="tw:px-4 tw:py-3 tw:text-right tw:text-gray-600">{e.node.rsvpCount ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SupporterPager({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  return (
    <div className="tw:inline-flex tw:items-center tw:gap-1">
      <button
        onClick={() => onPage(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="Previous page"
        className="tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:disabled:opacity-40 tw:hover:enabled:border-primary-400 tw:hover:enabled:text-primary-600"
      >
        <Icon type="chevron-left" className="tw:h-4 tw:w-4" />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          aria-current={p === page ? 'page' : undefined}
          className={clsx(
            'tw:h-8 tw:min-w-8 tw:rounded-lg tw:border tw:px-2 tw:text-sm tw:font-medium',
            p === page
              ? 'tw:border-primary-500 tw:bg-primary-50 tw:text-primary-700'
              : 'tw:border-gray-300 tw:bg-white tw:text-gray-600 tw:hover:border-primary-400 tw:hover:text-primary-600'
          )}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => onPage(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="Next page"
        className="tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:disabled:opacity-40 tw:hover:enabled:border-primary-400 tw:hover:enabled:text-primary-600"
      >
        <Icon type="chevron-right" className="tw:h-4 tw:w-4" />
      </button>
    </div>
  )
}

// ---- Communications tab: a sub-nav that switches between one view at a time ----
function CommunicationsTab({
  compact,
  focus,
  layout,
  barVariant,
}: {
  compact: boolean
  focus: { section: string; nonce: number } | null
  layout: CommsLayout
  barVariant: BarVariant
}) {
  const [section, setSection] = useState<string>('inbox')

  // The tab's dropdown (in the header nav) opens the tab directly at a chosen
  // sub-view. Keyed on `nonce` so re-picking the same item still switches to it.
  useEffect(() => {
    if (focus) setSection(focus.section)
  }, [focus?.nonce])

  // The view action bar's action is context-aware — it reflects the active sub-view.
  const barActions =
    section === 'email-blasts' ? (
      <Button rank="primary" leadingIcon="plus">
        New email blast
      </Button>
    ) : section === 'sms-blasts' ? (
      <Button rank="primary" leadingIcon="plus">
        New SMS blast
      </Button>
    ) : (
      <Button rank="secondary" trailingIcon="arrow-narrow-right">
        Open full inbox
      </Button>
    )

  const subNav = <CommsSubNav value={section} onChange={setSection} />

  return (
    <div className={clsx('tw:flex tw:flex-col', compact ? 'tw:gap-3' : 'tw:gap-4')}>
      {layout === 'subnavOnly' ? (
        // No titled bar — sub-nav on the left, the context action on the right.
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-gray-200 tw:pb-4">
          {subNav}
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">{barActions}</div>
        </div>
      ) : layout === 'integrated' ? (
        // Title + actions and the sub-nav share one header block (no divider between).
        <div className="tw:flex tw:flex-col tw:gap-4 tw:border-b tw:border-gray-200 tw:pb-4">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
            <BarHeading variant={barVariant} title="Communications" description="Inbox and broadcasts for this group." />
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">{barActions}</div>
          </div>
          {subNav}
        </div>
      ) : (
        // stacked — the action bar, then the sub-nav on its own row below.
        <>
          <ViewActionBar
            variant={barVariant}
            title="Communications"
            subtitle="Inbox and broadcasts for this group."
            actions={barActions}
          />
          {subNav}
        </>
      )}

      {section === 'inbox' && (
        // Simplified, non-interactive summary; the full inbox is the Group Inbox
        // redesign (group_inbox_generic_preview).
        <Section
          icon="message-chat-square"
          title="Inbox"
          description="Recent email & SMS conversations with this group's supporters."
          compact={compact}
          action={null}
        >
          <div className="tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:text-sm">
            <AwaitingCount n={TOTAL_AWAITING} />
            <span className="tw:text-gray-600">threads awaiting a response</span>
          </div>
          <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
            {INBOX_THREADS.map((thread, i) => (
              <InboxThreadRow key={i} thread={thread} />
            ))}
          </ul>
        </Section>
      )}

      {section === 'email-blasts' && (
        <Section
          icon="mail-01"
          title="Email blasts"
          description="Bulk emails sent to this group."
          compact={compact}
          action={null}
        >
          <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
            {EMAIL_BLASTS.map((b, i) => (
              <li
                key={i}
                className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-1 tw:py-3 tw:first:pt-0 tw:last:pb-0"
              >
                <div className="tw:min-w-0">
                  <div className="tw:text-sm tw:font-medium tw:text-gray-900">{b.subject}</div>
                  <div className="tw:text-xs tw:text-gray-500">
                    Sent {b.sentAt} · {b.recipients.toLocaleString()} recipients
                  </div>
                </div>
                <Badge color="success">{b.openRate} opened</Badge>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {section === 'sms-blasts' && (
        <Section
          icon="message-chat-square"
          title="SMS blasts"
          description="Bulk text messages sent to this group."
          compact={compact}
          action={null}
        >
          <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
            {SMS_BLASTS.map((b, i) => (
              <li key={i} className="tw:flex tw:flex-col tw:gap-0.5 tw:py-3 tw:first:pt-0 tw:last:pb-0">
                <p className="tw:m-0 tw:line-clamp-1 tw:text-sm tw:text-gray-900">{b.message}</p>
                <div className="tw:text-xs tw:text-gray-500">
                  Sent {b.sentAt} · {b.recipients.toLocaleString()} recipients
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  )
}

// Secondary nav within the Communications tab — pill style so it reads as
// subordinate to the primary tab strip above. Shares COMMS_SUB_ITEMS with the
// tab dropdown so the two stay in lockstep.
function CommsSubNav({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Communications views"
      className="tw:inline-flex tw:w-fit tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-gray-cool-50 tw:p-1"
    >
      {COMMS_SUB_ITEMS.map((it) => {
        const active = value === it.value
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.value)}
            className={clsx(
              'tw:inline-flex tw:items-center tw:gap-2 tw:rounded-md tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:transition-colors',
              active ? 'tw:bg-white tw:text-primary-700 tw:shadow-xs' : 'tw:text-gray-600 tw:hover:text-gray-900'
            )}
          >
            <Icon type={it.icon} className="tw:h-4 tw:w-4" aria-hidden />
            {it.label}
            {it.value === 'inbox' && <AwaitingCount n={TOTAL_AWAITING} />}
          </button>
        )
      })}
    </div>
  )
}

function InboxThreadRow({ thread }: { thread: (typeof INBOX_THREADS)[number] }) {
  const icon: IconTypes = thread.channel === 'email' ? 'mail-01' : 'message-chat-square'
  return (
    <li className="tw:flex tw:items-center tw:gap-3 tw:py-3 tw:first:pt-0 tw:last:pb-0">
      <span className="tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-gray-cool-50 tw:text-gray-500">
        <Icon type={icon} className="tw:h-4 tw:w-4" aria-hidden />
      </span>
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-sm tw:font-medium tw:text-gray-900">{thread.name}</span>
          <Badge color={thread.awaiting ? 'warning' : 'success'}>
            {thread.awaiting ? 'Awaiting' : 'Responded'}
          </Badge>
        </div>
        <p className="tw:m-0 tw:truncate tw:text-xs tw:text-gray-500">{thread.snippet}</p>
      </div>
      <span className="tw:shrink-0 tw:text-xs tw:text-gray-400">{thread.time}</span>
    </li>
  )
}

// ---- Reusable section card with a title and an Edit affordance ----
function Section({
  icon,
  title,
  description,
  action,
  compact,
  children,
}: {
  icon: IconTypes
  title: string
  description?: string
  action?: ReactNode
  compact: boolean
  children: ReactNode
}) {
  return (
    <section className="tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
      <div
        className={clsx(
          'tw:flex tw:items-start tw:justify-between tw:gap-4 tw:border-b tw:border-gray-100',
          compact ? 'tw:px-4 tw:py-3' : 'tw:px-5 tw:py-4'
        )}
      >
        <div className="tw:flex tw:items-start tw:gap-3">
          <span className="tw:mt-0.5 tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-gray-cool-50 tw:text-gray-500">
            <Icon type={icon} className="tw:h-4 tw:w-4" aria-hidden />
          </span>
          <div>
            <h2 className="tw:text-sm tw:font-bold tw:text-gray-900">{title}</h2>
            {description && <p className="tw:mt-0.5 tw:text-xs tw:text-gray-500">{description}</p>}
          </div>
        </div>
        {/* action === null → render no affordance; undefined → default Edit button */}
        <div className="tw:shrink-0">{action === undefined ? <EditButton /> : action}</div>
      </div>
      <div className={clsx(compact ? 'tw:px-4 tw:py-3' : 'tw:px-5 tw:py-4')}>{children}</div>
    </section>
  )
}

function EditButton() {
  return (
    <Button rank="secondary" size="sm" leadingIcon="pencil-02">
      Edit
    </Button>
  )
}

function Field({ label, value, children }: { label: string; value?: string; children?: ReactNode }) {
  return (
    <div>
      <dt className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</dt>
      <dd className="tw:mt-1 tw:text-sm tw:text-gray-900">{children ?? value}</dd>
    </div>
  )
}

// ---- Shared view-action-bar pattern (design variant) ----
// A consistent per-view header: title/subtitle on the left, a reserved actions
// slot on the right. Same shape as the page header, one level down — the one
// anchor that can exist on every tab regardless of stats/cards/lists below it.
function ViewActionBar({
  title,
  subtitle,
  actions,
  variant = 'titleDescription',
}: {
  title: string
  subtitle?: string
  actions: ReactNode
  variant?: BarVariant
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-gray-200 tw:pb-4">
      <BarHeading variant={variant} title={title} description={subtitle} />
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">{actions}</div>
    </div>
  )
}

// The left side of an action bar, switchable via the "Action bar content" toggle.
// Considerations behind the options:
//  - titleDescription: most context, but the title often duplicates the tab label
//  - titleOnly:        less redundant with the tab, keeps a clear anchor
//  - descriptionOnly:  the tab already names the view; the description adds the "why"
//  - actionsOnly:      leanest — rely entirely on the tab label, bar is just actions
function BarHeading({ variant, title, description }: { variant: BarVariant; title: string; description?: string }) {
  if (variant === 'actionsOnly') return <div aria-hidden />
  if (variant === 'descriptionOnly') {
    return <p className="tw:text-sm tw:font-medium tw:text-gray-700">{description ?? title}</p>
  }
  return (
    <div>
      <h2 className="tw:text-base tw:font-bold tw:text-gray-900">{title}</h2>
      {variant === 'titleDescription' && description && (
        <p className="tw:mt-0.5 tw:text-sm tw:text-gray-500">{description}</p>
      )}
    </div>
  )
}

// Generic overflow menu used by the view action bars.
function ActionMenu({ label = 'Actions', actions }: { label?: string; actions: Array<{ label: string; icon: IconTypes }> }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button rank="secondary" leadingIcon="dots-horizontal" trailingIcon="chevron-down">
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="tw:min-w-52">
        {actions.map((a) => (
          <DropdownMenuItem key={a.label} onSelect={() => {}}>
            <Icon type={a.icon} className="tw:text-gray-500" />
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Sample content so the Lists / Events tabs have something to preview alongside
// the action bar. Random-but-plausible data, no model behind it.
const LISTS = [
  { name: 'Weekly callers', type: 'Call list', count: 128, updated: '2 Jul 2026' },
  { name: 'Lapsed donors', type: 'Saved filter', count: 64, updated: '28 Jun 2026' },
  { name: 'Event no-shows', type: 'Call list', count: 32, updated: '20 Jun 2026' },
  { name: 'New this month', type: 'Saved filter', count: 41, updated: '1 Jul 2026' },
]

const EVENTS: Array<{ name: string; date: string; rsvps: number; status: 'Upcoming' | 'Past' }> = [
  { name: 'June regional assembly', date: '4 Jun 2026', rsvps: 86, status: 'Upcoming' },
  { name: 'Door-knock: Riverside', date: '12 Jun 2026', rsvps: 24, status: 'Upcoming' },
  { name: 'Volunteer social', date: '20 May 2026', rsvps: 51, status: 'Past' },
]

function ListsTab({ compact, barVariant }: { compact: boolean; barVariant: BarVariant }) {
  return (
    <div className={clsx('tw:flex tw:flex-col', compact ? 'tw:gap-3' : 'tw:gap-4')}>
      <ViewActionBar
        variant={barVariant}
        title="Lists"
        subtitle="Call lists and saved filters for this group."
        actions={
          <>
            <Button rank="primary" leadingIcon="plus">
              New list
            </Button>
            <ActionMenu
              actions={[
                { label: 'Import a list', icon: 'download-cloud' },
                { label: 'Manage saved filters', icon: 'funnel' },
              ]}
            />
          </>
        }
      />
      <div className="tw:flex tw:flex-col tw:gap-2">
        {LISTS.map((l) => (
          <div
            key={l.name}
            className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:px-4 tw:py-3 tw:shadow-xs"
          >
            <span className="tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-gray-cool-50 tw:text-gray-500">
              <Icon type="clipboard-minus" className="tw:h-4 tw:w-4" aria-hidden />
            </span>
            <div className="tw:min-w-0 tw:flex-1">
              <div className="tw:text-sm tw:font-medium tw:text-gray-900">{l.name}</div>
              <div className="tw:text-xs tw:text-gray-500">
                {l.type} · Updated {l.updated}
              </div>
            </div>
            <Badge color="gray">{l.count} supporters</Badge>
          </div>
        ))}
      </div>
    </div>
  )
}

function EventsTab({ compact, barVariant }: { compact: boolean; barVariant: BarVariant }) {
  return (
    <div className={clsx('tw:flex tw:flex-col', compact ? 'tw:gap-3' : 'tw:gap-4')}>
      <ViewActionBar
        variant={barVariant}
        title="Events"
        subtitle="Events organised by this group."
        actions={
          <>
            <Button rank="primary" leadingIcon="plus">
              New event
            </Button>
            <ActionMenu
              actions={[
                { label: 'Export attendees', icon: 'download-cloud' },
                { label: 'Open event calendar', icon: 'calendar' },
              ]}
            />
          </>
        }
      />
      <div className="tw:flex tw:flex-col tw:gap-2">
        {EVENTS.map((e) => (
          <div
            key={e.name}
            className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:px-4 tw:py-3 tw:shadow-xs"
          >
            <span className="tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-gray-cool-50 tw:text-gray-500">
              <Icon type="calendar" className="tw:h-4 tw:w-4" aria-hidden />
            </span>
            <div className="tw:min-w-0 tw:flex-1">
              <div className="tw:text-sm tw:font-medium tw:text-gray-900">{e.name}</div>
              <div className="tw:text-xs tw:text-gray-500">{e.date}</div>
            </div>
            <span className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-gray-500">
              <Icon type="ticket-01" className="tw:h-3.5 tw:w-3.5" aria-hidden />
              {e.rsvps}
            </span>
            <Badge color={e.status === 'Upcoming' ? 'primary' : 'gray'}>{e.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- Design-explorer segmented control (shared shape from GroupInboxIndex) ----
function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default GroupShowPreview
